import React, { useState, useCallback } from 'react';
import { File } from './types';
import { usePrivacyData } from './usePrivacyData';
import { PdfIcon } from './components/ui/PdfIcon';
import { PlaceholderIcon } from './components/ui/PlaceholderIcon';
import { ActionLinks } from './components/ui/ActionLinks';
import PdfViewerModal from './PdfViewerModal';

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
  <p className="text-center text-red-600 p-4">{message}</p>
);

const NoDataMessage: React.FC = () => (
  <div className="p-8 bg-white font-sans max-w-5xl mx-auto w-full shadow-md rounded-lg">
    <div>
      <h2 className="text-center font-bold text-xl text-gray-800">Avisos de Privacidad</h2>
    </div>
    <div className="space-y-10">
      <div className="hidden md:grid grid-cols-3 gap-x-8 items-center ">
        <div className="text-lg font-bold text-gray-800 md:justify-self-start">Proceso</div>
        <div className="text-lg font-bold text-gray-800 text-center">Simplificado</div>
        <div className="text-lg font-bold text-gray-800 text-center">Integral</div>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-y-4 md:gap-x-8 items-center border-t pt-4 md:border-none md:pt-0">
      <div className="text-lg text-gray-700 font-semibold justify-self-center md:justify-self-start"></div>
      <div className="flex flex-col items-center">
        Sin avisos de privacidad disponibles
      </div>
      <div className="flex flex-col items-center"></div>
    </div>
  </div>
);

const DocumentGrid: React.FC<{
  documentsData: any[];
  onViewPdf: (url: string) => void;
}> = ({ documentsData, onViewPdf }) => {
  const renderCellContent = useCallback((cellData: File, label: string) => {
    if (!cellData) return null;
    
    switch(cellData.type) {
      case 'pdf':
        return (
          <>
            <PdfIcon />
            {cellData.actions && (
              <ActionLinks 
                url={cellData.url || ''} 
                label={label} 
                onViewPdf={onViewPdf}
              />
            )}
          </>
        );
      case 'placeholder':
        return <PlaceholderIcon />;
      case 'empty':
      default:
        return null;
    }
  }, [onViewPdf]);

  return (
    <div className="space-y-10">
      <div className="hidden md:grid grid-cols-3 gap-x-8 items-center">
        <div className="text-lg font-bold text-gray-800 md:justify-self-start">Proceso</div>
        <div className="text-lg font-bold text-gray-800 text-center">Simplificado</div>
        <div className="text-lg font-bold text-gray-800 text-center">Integral</div>
      </div>

      {documentsData.map((doc) => (
        <div 
          key={doc.id} 
          className="grid grid-cols-1 md:grid-cols-3 gap-y-4 md:gap-x-8 items-center border-t pt-4 md:border-none md:pt-0"
        >
          <div className="text-lg text-gray-700 font-semibold justify-self-center md:justify-self-start">
            {doc.label}
          </div>
          
          <div className="flex flex-col items-center">
            <div className="font-bold text-gray-600 md:hidden mb-2">Simplificado</div>
            {renderCellContent(doc.simplified, doc.simplified.name_file ?? 'AvisoSimplificado.pdf')}
          </div>

          <div className="flex flex-col items-center">
            <div className="font-bold text-gray-600 md:hidden mb-2">Integral</div>
            {renderCellContent(doc.integral, doc.integral.name_file ?? 'AvisoIntegral.pdf')}
          </div>
        </div>
      ))}
    </div>
  );
};

const VistaPrivacidad: React.FC = () => {
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const { localizedData, documentsData, hasData, hasFiles } = usePrivacyData();

  const handleViewPdf = useCallback((url: string) => {
    setSelectedPdf(url);
  }, []);

  const handleClosePdf = useCallback(() => {
    setSelectedPdf(null);
  }, []);

  // Estados de error
  if (!localizedData || !localizedData?.data) {
    return <ErrorMessage message="Error: no hay datos localizados." />;
  }

  if (!hasData) {
    return <ErrorMessage message="Hubo un error con el servidor, por favor inténtalo de nuevo." />;
  }

  if (!hasFiles) {
    return <NoDataMessage />;
  }

  return (
    <div className="p-8 bg-white font-sans max-w-5xl mx-auto w-full shadow-md rounded-lg">
      <div>
        <h2 className="text-center font-bold text-xl text-gray-800">Avisos de Privacidad</h2>
      </div>
      
      <DocumentGrid 
        documentsData={documentsData}
        onViewPdf={handleViewPdf}
      />

      {selectedPdf && (
        <PdfViewerModal
          fileUrl={selectedPdf}
          onClose={handleClosePdf}
        />
      )}
    </div>
  );
};

export default VistaPrivacidad;
